// src/components/home/NewArrivals.tsx
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowUpRight, Sparkles } from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

export type NewProduct = {
  product_id: number;
  name: string;
  base_price: number;
  image_url: string;
  slug?: string; // optional, không bắt buộc
};

type Props = {
  products: NewProduct[];

  /** optional override text */
  kicker?: string;          // "New arrivals"
  title?: string;           // "Curated pieces, ..."
  description?: string;     // paragraph
  topCtaLabel?: string;     // "View all"
  bottomCtaLabel?: string;  // "View full collection"

  /** optional routes */
  topCtaTo?: string;        // default "/collection"
  bottomCtaTo?: string;     // default "/collection"

  /** optional: route builder for product */
  productTo?: (p: NewProduct) => string; // default `/product/${id}`
};

function cn(...x: Array<string | false | undefined | null>) {
  return x.filter(Boolean).join(" ");
}

function formatVnd(v: number) {
  return (v ?? 0).toLocaleString("vi-VN") + "₫";
}

function usePrefersReducedMotion() {
  return useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  }, []);
}

export default function NewArrivals({
  products,

  kicker,
  title,
  description,
  topCtaLabel,
  bottomCtaLabel,
  topCtaTo = "/collection",
  bottomCtaTo = "/collection",
  productTo,
}: Props) {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const reducedMotion = usePrefersReducedMotion();

  // ✅ cache ảnh lỗi theo product_id để không spam onError/load lại
  const [imgFailed, setImgFailed] = useState<Record<string, boolean>>({});

  const finalKicker = kicker ?? t("newArrivals.kicker");
  const finalTitle = title ?? t("newArrivals.title");
  const finalDesc = description ?? t("newArrivals.desc");
  const finalTopCta = topCtaLabel ?? t("newArrivals.ctaTop");
  const finalBottomCta = bottomCtaLabel ?? t("newArrivals.ctaBottom");

  const resolveProductLink =
    productTo ?? ((p: NewProduct) => `/product/${p.product_id}`);

  useLayoutEffect(() => {
    if (!rootRef.current) return;
    if (reducedMotion) return;

    const ctx = gsap.context(() => {
      const header =
        rootRef.current?.querySelector<HTMLElement>("[data-od-header]");
      if (header) {
        gsap.fromTo(
          header,
          { y: 16, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
            ease: "power3.out",
            scrollTrigger: {
              trigger: header,
              start: "top 78%",
              once: true,
            },
          }
        );
      }

      gsap.utils.toArray<HTMLElement>("[data-od-card]").forEach((card, i) => {
        gsap.fromTo(
          card,
          { y: 18, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.7,
            delay: i * 0.03,
            ease: "power3.out",
            scrollTrigger: {
              trigger: card,
              start: "top 86%",
              once: true,
            },
          }
        );
      });
    }, rootRef);

    return () => ctx.revert();
  }, [reducedMotion, products.length]);

  return (
    <section
      ref={rootRef}
      className="relative bg-brand-ivory text-brand-brown py-16 sm:py-20"
    >
      {/* subtle wash */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-ivory via-brand-ivory to-brand-ivory" />
        <div className="absolute inset-0 bg-gradient-to-tr from-brand-gold/10 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-bl from-brand-brown/6 via-transparent to-transparent" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 sm:px-8 lg:px-16">
        {/* HEADER */}
        <div
          data-od-header
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10"
        >
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-3">
              <span className="h-9 w-9 rounded-2xl border border-brand-brown/10 bg-brand-brown/5 grid place-items-center">
                <Sparkles className="w-4 h-4 text-brand-gold" />
              </span>
              <p className="text-[10px] uppercase tracking-[0.62em] text-brand-brown/55">
                {finalKicker}
              </p>
            </div>

            <h2 className="mt-8 text-[30px] sm:text-[38px] lg:text-[44px] leading-[1.08] uppercase tracking-[0.18em]">
              {finalTitle}
            </h2>

            <p className="mt-5 text-sm leading-[1.9] text-brand-brown/60 max-w-xl">
              {finalDesc}
            </p>
          </div>

          <Link
            to={topCtaTo}
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.55em]
                       text-brand-brown/55 hover:text-brand-brown transition"
          >
            {finalTopCta} <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        {/* GRID */}
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          {products.map((p, i) => {
            const key = String(p.product_id);
            const imgSrc = imgFailed[key] ? "/images/placeholder.jpg" : p.image_url;

            return (
              <Link
                key={p.product_id}
                to={resolveProductLink(p)}
                data-od-card
                className={cn(
                  "group block",
                  i % 4 === 1 && "lg:translate-y-6",
                  i % 4 === 2 && "lg:translate-y-3"
                )}
              >
                <div
                  className="rounded-[26px] sm:rounded-[30px] overflow-hidden border border-brand-brown/10
                             bg-brand-ivory/70 backdrop-blur-md
                             shadow-[0_30px_90px_rgba(43,31,26,0.10)]
                             transition"
                >
                  {/* IMAGE */}
                  <div className="relative aspect-[4/5] bg-brand-brown/5 overflow-hidden">
                    <img
                      src={imgSrc}
                      alt={p.name}
                      className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.04]"
                      loading="lazy"
                      decoding="async"
                      onError={() => setImgFailed((m) => ({ ...m, [key]: true }))}
                    />

                    {/* gloss */}
                    <div
                      className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700
                                 bg-gradient-to-tr from-brand-ivory/20 via-transparent to-brand-ivory/10"
                    />

                    {/* bottom fade for text readability */}
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-brand-brown/25 via-transparent to-transparent" />
                  </div>

                  {/* INFO */}
                  <div className="p-4 sm:p-5">
                    <p className="text-[10px] uppercase tracking-[0.45em] text-brand-brown/70 line-clamp-1">
                      {p.name}
                    </p>

                    <div className="mt-3 flex items-center justify-between gap-3">
                      <p className="text-sm text-brand-brown/65">
                        {formatVnd(p.base_price)}
                      </p>

                      <span
                        className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.55em]
                                   text-brand-brown/45 group-hover:text-brand-gold transition"
                      >
                        {t("newArrivals.view")} <ArrowUpRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* CTA BOTTOM */}
        <div className="mt-12 flex justify-center">
          <Link
            to={bottomCtaTo}
            className="inline-flex items-center gap-3 h-12 px-7 rounded-full
                       border border-brand-brown/12 bg-transparent
                       text-[10px] uppercase tracking-[0.55em] text-brand-brown/70
                       hover:border-brand-brown/25 hover:text-brand-brown hover:bg-brand-brown/5 transition"
          >
            {finalBottomCta} <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
